<template>
  <v-card min-width="680" elevation="12"
          rounded="lg"
          class="mb-4"
  >
    <v-overlay :value="overlay">
      <v-progress-circular
        indeterminate
        size="64"
      />
    </v-overlay>
    <v-toolbar
            flat
            dense
            color="color_green"
            dark
          >
            <v-toolbar-title>Policy Details</v-toolbar-title>
    </v-toolbar>
    <v-card-text>
      <v-row>
        <v-col>
          <v-form>
            <v-text-field
              v-model="form.policy_name"
              label="Policy Name"
              :rules="policy_name_rules"
              :counter="255"
            />
            <v-text-field
              v-model="form.policy_description"
              label="Policy Description"
              :rules="policy_description_rules"
              :counter="255"
            />
          </v-form>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-card
            id="policy-quota"
            outlined
          >
            <v-card-title>Quota</v-card-title>
            <v-card-text>
              <v-row align="center">
                <v-col
                  cols="12"
                  sm="4"
                >
                  <v-radio-group
                    v-model="quotaLimited"
                    mandatory
                    label="Quota limit"
                    color="color_green"
                    hide-details
                    @change="changeQuotaRadioBtn"
                  >
                    <v-radio
                      label="Unlimited"
                      :ripple="true"
                      value="unlimited"
                      color="color_green"
                    />
                    <v-radio
                      label="Limited"
                      :ripple="true"
                      value="limited"
                      color="color_green"
                    />
                  </v-radio-group>
                </v-col>
                <v-col
                  v-if="quotaLimited === 'limited'"
                  cols="12"
                  sm="4"
                >
                  <v-text-field
                    v-model="form.quota_limit"
                    :rules="quota_limit_rules"
                    type="number"
                    min="0"
                    outlined
                    dense
                    label="Quota limit"
                  />
                </v-col>
              </v-row>

              <v-divider class="my-4" />

              <v-row>
                <v-col
                  cols="12"
                  sm="6"
                >
                  <v-radio-group
                    v-model="form.quota_metric"
                    mandatory
                    label="Quota metric"
                    color="color_green"
                    hide-details
                  >
                    <v-radio
                      label="Numero di chiamate"
                      :ripple="true"
                      value="calls"
                      color="color_green"
                    />
                    <v-radio
                      label="Crediti pesati"
                      :ripple="true"
                      value="credits"
                      color="color_green"
                    />
                  </v-radio-group>
                </v-col>
                <v-col
                  cols="12"
                  sm="6"
                >
                  <v-radio-group
                    v-model="form.quota_period"
                    mandatory
                    label="Quota period"
                    color="color_green"
                    hide-details
                  >
                    <v-radio
                      label="Giornaliera"
                      :ripple="true"
                      value="daily"
                      color="color_green"
                    />
                    <v-radio
                      label="Mensile"
                      :ripple="true"
                      value="monthly"
                      color="color_green"
                    />
                  </v-radio-group>
                </v-col>
              </v-row>

              <v-row v-if="form.quota_period === 'monthly'">
                <v-col
                  cols="12"
                  sm="6"
                >
                  <v-text-field
                    v-model="form.max_calls_per_day"
                    :rules="max_calls_per_day_rules"
                    type="number"
                    min="0"
                    outlined
                    dense
                    label="Max chiamate al giorno (opzionale)"
                    hint="Limite di sicurezza giornaliero aggiuntivo, indipendente dalla quota mensile"
                    persistent-hint
                  />
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-btn
            outlined
            color="color_green"
            :disabled="checkForm"
            @click="confirmPolicy"
          >
            Confirm api policy details
          </v-btn>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-card
            :disabled="disableWSCard || !form.policy_name"
            outlined
          >
            <v-toolbar
              flat
              dark
              color="color_green"
            >
              <v-toolbar-title>Workspaces</v-toolbar-title>
              <v-divider
                class="mx-4"
                inset
                vertical
              />
              <v-spacer />
              <cu-workspace-dialog
                :key="componentKey"
                :type="'new'"
                @closeDialogEvent="forceRerender"
              />
            </v-toolbar>
            <v-data-table
              :headers="workspaceTableHeaders"
              :items="geoserver_data.workspaces"
              item-key="workspace_name"
              dense
              disable-pagination
              hide-default-footer
            >
              <template v-slot:[`item.start_time`]="{ item }">
                {{ item.start_time || '-' }}
              </template>
              <template v-slot:[`item.stop_time`]="{ item }">
                {{ item.stop_time || '-' }}
              </template>
              <template v-slot:[`item.layers_count`]="{ item }">
                {{ layerCounts[item.workspace_name] !== undefined ? layerCounts[item.workspace_name] : '…' }}
              </template>
              <template v-slot:[`item.actions`]="{ item }">
                <cu-workspace-dialog
                  :key="componentKey"
                  :ws="item"
                  @closeDialogEvent="forceRerender"
                />
                <v-btn icon>
                  <delete
                    :id="item.name"
                    @deleted="deleteClick(item)"
                  />
                </v-btn>
              </template>
              <template v-slot:no-data>
                <span>No workspace linked yet</span>
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { required, manageLenghtError, getRegExp } from '@/formRules.js';
import { getDBLayers } from '@/api/api.js';

export default {
  components: {
    cuWorkspaceDialog: () => import('@/components/policy/CUWorkspaceDialog'),
    delete: () => import('@/components/utils/Delete')
  },
  props: {
    form: {
      type: Object,
      default: null
    },
    disableWScard: {
      type: Boolean,
      default: null
    }
  },
  data() {
    return {
      overlay: false,
      componentKey: 0,
      policy_name_rules: [...required, ...manageLenghtError(255)],
      policy_description_rules: [...manageLenghtError(255)],
      quota_limit_rules: [...required, ...getRegExp('numbers_only')],
      max_calls_per_day_rules: [
        v => !v || /^\d+$/.test(v) || 'Numbers only allowed'
      ],
      quotaLimited: 'unlimited',
      disableWSCard: false,
      layerCounts: {},
      workspaceTableHeaders: [
        { text: 'Workspace Name', value: 'workspace_name' },
        { text: 'Date Type', value: 'date_type' },
        { text: 'Start', value: 'start_time' },
        { text: 'Stop', value: 'stop_time' },
        { text: 'Layers', value: 'layers_count', sortable: false },
        { text: 'Actions', value: 'actions', sortable: false, width: 100 }
      ]
    };
  },
  computed: {
    ...mapState(['policy', 'geoserver_data']),
    checkForm() {
      const charLength = this.form.policy_name.length <= 0 || this.form.policy_name.length > 255;
      const invalidQuotaLimit = this.quotaLimited === 'limited' && !this.form.quota_limit;

      return charLength || invalidQuotaLimit;
    }
  },
  watch: {
    form() {
      this.syncQuotaLimited();

      if (this.policy.isModifingPolicy == true) {
        this.storeWorkspacesByPolicy(this.form.policy_id);
      } else {
        this.geoserver_data.workspaces = [];
      }
    },
    'geoserver_data.workspaces': {
      immediate: true,
      handler(workspaces) {
        this.loadLayerCounts(workspaces);
      }
    }
  },
  created() {
    this.overlay = true;

    this.syncQuotaLimited();

    if (this.policy.isModifingPolicy == true) {
      this.storeWorkspacesByPolicy(this.form.policy_id);
      this.disableWSCard = false;
    } else {
      this.disableWSCard = true;
    }

    this.overlay = false;
  },
  methods: {
    ...mapActions([
      'storeNewPolicy',
      'storeUpdatedPolicy',
      'storeWorkspacesByPolicy',
      'storeWorkspace',
      'deleteScrollWorkspace',
      'storeIsModifingPolicy',
      'storeToastMessage'
    ]),
    async confirmPolicy() {
      this.overlay = true;

      this.form.quota_limit = this.toNullableInt(this.form.quota_limit);
      this.form.max_calls_per_day = this.form.quota_period === 'monthly'
        ? this.toNullableInt(this.form.max_calls_per_day)
        : null;

      if (this.policy.isModifingPolicy) {
        await this.storeUpdatedPolicy(this.form);
      } else {
        await this.storeNewPolicy(this.form);
        this.$emit('updateConfirmedPolicy');
        this.disableWSCard = false;
      }

      this.overlay = false;
    },
    syncQuotaLimited() {
      this.quotaLimited = this.form.quota_limit ? 'limited' : 'unlimited';
    },
    toNullableInt(value) {
      if (value === null || value === undefined || value === '') {
        return null;
      }

      const parsed = parseInt(value, 10);

      return Number.isNaN(parsed) ? null : parsed;
    },
    changeQuotaRadioBtn() {
      this.form.quota_limit = this.quotaLimited === 'unlimited' ? null : '';
    },
    forceRerender() {
      this.componentKey += 1;
      this.loadLayerCounts(this.geoserver_data.workspaces);
    },
    deleteClick(ws) {
      const wsToDelete = {
        workspace_name: ws.workspace_name,
        policy_id: this.form.policy_id
      };
      this.deleteScrollWorkspace(wsToDelete);
    },
    async loadLayerCounts(workspaces) {
      if (!workspaces || workspaces.length === 0) {
        return;
      }

      await Promise.all(workspaces.map(async ws => {
        try {
          const resp = await getDBLayers(ws.workspace_name, this.form.policy_id);

          this.$set(this.layerCounts, ws.workspace_name, (resp.data.listDBLayers || []).length);
        } catch {
          this.$set(this.layerCounts, ws.workspace_name, 0);
        }
      }));
    }
  }
};
</script>
